import { useEffect, useRef, useState } from 'react';

interface ScrollDragContainerProps {
	className: string;
	children: React.ReactNode;
	leftArrow?: (handler: () => void, isAtStart: boolean) => React.ReactNode; 
	rightArrow?: (handler: () => void, isAtEnd: boolean) => React.ReactNode;
}

export const ScrollDragContainer = ({
	children,
	className,
	leftArrow,
	rightArrow,
}: ScrollDragContainerProps) => {
	const scrollContainerRef = useRef<HTMLDivElement | null>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [startX, setStartX] = useState(0);
	const [scrollLeft, setScrollLeft] = useState(0);
	const [isAtStart, setIsAtStart] = useState(true);
	const [isAtEnd, setIsAtEnd] = useState(false);
	
	const checkScrollPosition = () => {
		if (scrollContainerRef.current) {
			const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
			const isHorizontallyScrollable = scrollWidth > clientWidth;
			setIsAtStart(scrollLeft === 0);
			setIsAtEnd((scrollLeft + clientWidth >= scrollWidth - 1) || !isHorizontallyScrollable);
		}
	};
	const handleMouseDown = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		if (!scrollContainerRef.current) return;
		setIsDragging(true);
		setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
		setScrollLeft(scrollContainerRef.current.scrollLeft);
	};

	const handleMouseMove = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		if (!isDragging || !scrollContainerRef.current) return;

		e.preventDefault();
		const x = e.pageX - scrollContainerRef.current.offsetLeft;
		const walk = (x - startX) * 2;
		scrollContainerRef.current.scrollLeft = scrollLeft - walk;
	};

	const handleMouseUpOrLeave = () => {
		if (scrollContainerRef.current) {
			setIsDragging(false);
		}
	};

	const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
		if (!scrollContainerRef.current) return;

		setIsDragging(true);
		setStartX(e.touches[0].pageX - scrollContainerRef.current.offsetLeft);
		setScrollLeft(scrollContainerRef.current.scrollLeft);
	};

	const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
		if (!isDragging || !scrollContainerRef.current) return;

		const x = e.touches[0].pageX - scrollContainerRef.current.offsetLeft;
		const walk = (x - startX) * 2; // Adjust scroll speed (multiplier)
		scrollContainerRef.current.scrollLeft = scrollLeft - walk;
	};

	const handleTouchEnd = () => {
		setIsDragging(false);
	};

	const scrollLeftHandler = () => {
		if (scrollContainerRef.current) {
			scrollContainerRef.current.scrollBy({
				left: -scrollContainerRef.current.clientWidth,
				behavior: 'smooth',
			});
		}
	};

	const scrollRightHandler = () => {
		if (scrollContainerRef.current) {
			scrollContainerRef.current.scrollBy({
				left: scrollContainerRef.current.clientWidth,
				behavior: 'smooth',
			});
		}
	};

	useEffect(() => {
		checkScrollPosition();
	}, []);
  
	return (
		<>  
			{leftArrow ? leftArrow(scrollLeftHandler, isAtStart) : null}
			<div
				className={className}
				ref={scrollContainerRef}
				onMouseDown={handleMouseDown}
				onMouseMove={handleMouseMove}
				onMouseUp={handleMouseUpOrLeave}
				onMouseLeave={handleMouseUpOrLeave}
				onTouchStart={handleTouchStart}
				onTouchMove={handleTouchMove}
				onTouchEnd={handleTouchEnd}
				onScroll={checkScrollPosition}
			>
				{children}
			</div>
			{rightArrow ? rightArrow(scrollRightHandler, isAtEnd) : null}
		</>
	);
};